import { useMemo, useState } from 'react';
import { Box, Button, Chip, Collapse, Typography } from '@mui/material';
import { Braces, ChevronDown, Database } from 'lucide-react';

import {
  ContextFieldGroups,
  groupContextFields,
} from '$layouts/v2/components/ContextData.jsx';
import { Stack } from '$layouts/v2/components/Stack.jsx';
import { type FieldInfo } from '$layouts/v2/helpers/context.js';

import { StepSection } from './StepFormPrimitives';

export function DataInSection({
  title,
  description,
  availableFields,
  inputFieldNames,
  selected,
  onChange,
  readOnly = false,
}: {
  title: string;
  description: string;
  availableFields: FieldInfo[];
  inputFieldNames: string[];
  selected: string[];
  onChange: (fields: string[]) => void;
  readOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selectedNames = useMemo(() => new Set(selected), [selected]);
  const fieldsByName = useMemo(
    () => new Map(availableFields.map((field) => [field.name, field])),
    [availableFields],
  );
  const selectableFields = useMemo(
    () =>
      inputFieldNames.map(
        (name) =>
          fieldsByName.get(name) ?? {
            name,
            type: 'text' as const,
            producedBy: 'unavailable',
            producedByLabel: 'No longer available',
          },
      ),
    [fieldsByName, inputFieldNames],
  );
  const selectedFields = selectableFields.filter((field) =>
    selectedNames.has(field.name),
  );
  const selectedGroups = groupContextFields(selectedFields);

  const toggleField = (field: FieldInfo) => {
    const next = new Set(selected);
    if (next.has(field.name)) {
      next.delete(field.name);
    } else {
      next.add(field.name);
    }
    onChange([...next]);
  };

  return (
    <StepSection title={title} description={description}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {selectedFields.length === 0
              ? 'No data selected'
              : `${selectedFields.length} of ${selectableFields.length} fields included`}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {readOnly
              ? 'These are the fields this step receives.'
              : 'Choose only the data this step needs.'}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="text"
          startIcon={<Braces size={14} />}
          endIcon={
            <ChevronDown
              size={14}
              style={{ transform: open ? 'rotate(180deg)' : undefined }}
            />
          }
          onClick={() => setOpen((current) => !current)}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
        >
          {open ? 'Hide data' : readOnly ? 'View data' : 'Choose data'}
        </Button>
      </Stack>

      {selectedGroups.length > 0 && !open && (
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          sx={{ flexWrap: 'wrap' }}
        >
          {selectedGroups.map((group) => (
            <Chip
              key={group.source.id}
              size="small"
              variant="outlined"
              label={`${group.source.label} · ${group.fields.length}`}
              title={group.fields.map((field) => field.name).join(', ')}
            />
          ))}
        </Stack>
      )}

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            pt: 1.25,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              mb: 0.75,
            }}
          >
            {readOnly
              ? 'Included data is highlighted.'
              : 'Click a field to include it or remove it.'}
          </Typography>
          <ContextFieldGroups
            fields={selectableFields}
            selectedNames={selectedNames}
            selectedLabel="included"
            selectionColor="primary"
            onFieldClick={readOnly ? undefined : toggleField}
            emptyLabel="No data is available before this step yet."
            compact
            maxHeight={260}
          />
        </Box>
      </Collapse>
    </StepSection>
  );
}

export function ContextPanel({ fields }: { fields: FieldInfo[] }) {
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        pt: 1.25,
        mt: 1.5,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={0.75}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
      >
        <Button
          size="small"
          color="inherit"
          startIcon={<Database size={14} />}
          endIcon={
            <ChevronDown
              size={14}
              style={{ transform: open ? 'rotate(180deg)' : undefined }}
            />
          }
          onClick={() => setOpen((current) => !current)}
          sx={{
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          {open ? 'Hide context' : `Available context (${fields.length})`}
        </Button>
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Stack spacing={1.5} sx={{ mt: 1.25 }}>
          <Box>
            <ContextFieldGroups fields={fields} compact maxHeight={280} />
          </Box>
        </Stack>
      </Collapse>
    </Box>
  );
}
